import { useQuery } from "@tanstack/react-query";
import { getBoardRoleName } from "enums/BoardRoleEnum";
import { BoardRoleEnum } from "enums/BoardRoleEnum";
import { createLogger } from "logging";
import { ConfirmDeleteTarget } from "models/ConfirmDeleteTarget";
import { UserLookupModel } from "models/UserLookupModel";
import { useApi, useBoardSettings, useToasts, useTranslations } from "providers";
import React, { PropsWithChildren, useCallback, useMemo, useState } from "react";

import { ConfirmDeleteGroupMember } from "./components/ConfirmDeleteGroupMember";
import { EditBoardGroupPanel } from "./components/EditBoardGroupPanel";
import { EditBoardGroupPanelContext } from "./EditBoardGroupPanelContext";

const logger = createLogger("EditBoardGroupPanelProvider");

export const EditBoardGroupPanelProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const i18n = useTranslations();
  const { boardId, reloadBoardSettings } = useBoardSettings();

  const { boardMembersApi } = useApi();
  const toasts = useToasts();

  const [isOpen, setIsOpen] = useState(false);
  const [role, setRole] = useState<BoardRoleEnum | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState<number | null>(null);
  const [confirmDeleteMember, setConfirmDeleteMember] = useState<ConfirmDeleteTarget | undefined>(undefined);

  const queryResult = useQuery({
    queryKey: [`BoardGroupMembers|${boardId}|${role}`],
    queryFn: async () => {
      const data = await boardMembersApi.getGroupMembers(boardId, BoardRoleEnum[role!].toLowerCase());
      logger.trace("Loaded", data);
      return data;
    },
    enabled: isOpen && role !== null && !!boardId,
    staleTime: 0,
  });

  const reloadGroupMembers = queryResult.refetch;
  const isLoading = queryResult.isFetching;
  const members = useMemo(() => queryResult.data || [], [queryResult.data]);

  const openEditBoardGroupPanel = useCallback((newRole: BoardRoleEnum) => {
    setRole(newRole);
    setIsOpen(true);
  }, []);

  const closePanel = useCallback(() => {
    setIsOpen(false);
    setRole(null);
    setConfirmDeleteMember(undefined);
    reloadBoardSettings();
  }, [reloadBoardSettings]);

  const handleAdd = useCallback(
    async (user: UserLookupModel) => {
      if (!boardId || !role || !user.upn) return;
      const roleName = BoardRoleEnum[role].toLowerCase();
      const roleLabel = getBoardRoleName(role, i18n);
      setIsAdding(true);
      try {
        await boardMembersApi.addGroupMember(boardId, roleName, user.upn);
        toasts.addSuccess(roleLabel, user.displayName ?? user.upn);
      } catch (error) {
        toasts.addFailure(roleLabel, user.displayName ?? user.upn, error);
      } finally {
        setIsAdding(false);
        reloadGroupMembers();
      }
    },
    [boardId, role, i18n, boardMembersApi, toasts, reloadGroupMembers]
  );

  const panelTitle = useMemo(
    () => i18n.Page_Group_Title.replace("{0}", role !== null ? getBoardRoleName(role, i18n) : ""),
    [role, i18n]
  );

  const contextValue = useMemo(
    () => ({
      isOpen,
      role,
      isAdding,
      isLoading,
      deletingUserId,
      confirmDeleteMember,
      members,
      panelTitle,
      openEditBoardGroupPanel,
      closePanel,
      handleAdd,
      reloadGroupMembers,
      setDeletingUserId,
      setConfirmDeleteMember,
    }),
    [
      isOpen,
      role,
      isAdding,
      isLoading,
      deletingUserId,
      confirmDeleteMember,
      members,
      panelTitle,
      openEditBoardGroupPanel,
      closePanel,
      handleAdd,
      reloadGroupMembers,
      setDeletingUserId,
      setConfirmDeleteMember,
    ]
  );

  return (
    <EditBoardGroupPanelContext.Provider value={contextValue}>
      {children}
      <EditBoardGroupPanel />
      <ConfirmDeleteGroupMember />
    </EditBoardGroupPanelContext.Provider>
  );
};
